import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Circle, Clock, ChevronDown, ChevronUp, ListChecks, X, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { currentStep, type NextStep } from '@/lib/nextSteps';
import type { UseNextStepsResult } from '@/hooks/useNextSteps';

function StepIcon({ status }: { status: NextStep['status'] }) {
  if (status === 'done') {
    return (
      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 shadow-sm shrink-0">
        <Check className="w-3 h-3 text-white" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'pending') return <Clock className="w-4 h-4 text-sky-500 shrink-0" />;
  return <Circle className="w-4 h-4 text-slate-300 shrink-0" />;
}

export function NextStepsCard({ nextSteps, title = 'Your next steps', className }: {
  nextSteps: UseNextStepsResult;
  title?: string;
  className?: string;
}) {
  const { steps, allDone, isReady, dismissed, dismiss } = nextSteps;
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);

  if (!isReady || dismissed) return null;

  const active = currentStep(steps);
  const doneCount = steps.filter((s) => s.status === 'done').length;
  const progressPct = Math.round((doneCount / steps.length) * 100);

  return (
    <div data-tour="next-steps" className={cn('rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
          <ListChecks className="w-4.5 h-4.5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
          <p className="text-xs text-slate-500">
            {allDone ? 'All done — you\u2019re fully set up!' : `${doneCount} of ${steps.length} completed`}
          </p>
        </div>
        {/* Progress */}
        <div className="hidden sm:block w-28 h-1.5 rounded-full bg-slate-100 overflow-hidden shrink-0">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <button
          type="button"
          aria-label={collapsed ? 'Expand next steps' : 'Collapse next steps'}
          onClick={() => setCollapsed((c) => !c)}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
        {allDone && (
          <button
            type="button"
            aria-label="Dismiss next steps"
            onClick={dismiss}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="border-t border-slate-100 px-5 py-4">
          <ol className="space-y-2.5">
            {steps.map((step) => {
              const isActive = step.id === active?.id;
              return (
                <li key={step.id} className={cn('flex items-start gap-2.5', isActive && 'rounded-xl -mx-2 px-2 py-2 bg-indigo-50/60')}>
                  <div className="mt-0.5"><StepIcon status={step.status} /></div>
                  <div className="flex-1 min-w-0">
                    <span className={cn(
                      'text-sm',
                      step.status === 'done' ? 'text-slate-500' : 'text-slate-700',
                      isActive && 'font-semibold text-slate-800',
                    )}>
                      {step.label}
                    </span>
                    {isActive && step.hint && (
                      <p className="text-xs text-slate-500 mt-0.5">{step.hint}</p>
                    )}
                    {isActive && step.status !== 'pending' && (
                      <Button
                        size="sm"
                        className="mt-2 h-8"
                        onClick={() => navigate(step.route)}
                      >
                        Go <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
